import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Pencil, Eraser, Trash2, X, Circle, Square, 
  Minus, MousePointer, Undo, Redo, Highlighter
} from 'lucide-react';

const COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'White', value: '#ffffff' },
  { name: 'Black', value: '#000000' },
];

const TOOLS = {
  POINTER: 'pointer',
  PENCIL: 'pencil',
  HIGHLIGHTER: 'highlighter',
  LINE: 'line',
  CIRCLE: 'circle',
  RECTANGLE: 'rectangle',
  ERASER: 'eraser',
};

// Quick-pick colors for highlighter tool
const HIGHLIGHTER_COLORS = ['#fde047', '#86efac', '#fda4af', '#93c5fd'];

const DrawingCanvas = ({ isActive, onClose, slideIndex }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState(TOOLS.PENCIL);
  const [color, setColor] = useState('#ef4444');
  const [lineWidth, setLineWidth] = useState(4);
  const [showToolbar, setShowToolbar] = useState(true);
  const [isReady, setIsReady] = useState(false);
  
  // For shapes
  const [startPoint, setStartPoint] = useState(null);
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Initialize canvas when component becomes active
  useEffect(() => {
    if (!isActive) {
      setIsReady(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set canvas size to match window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    
    setIsReady(true);
    
    // Save initial state
    const imageData = canvas.toDataURL();
    setHistory([imageData]);
    setHistoryIndex(0);
    
    // Handle window resize
    const handleResize = () => {
      const imageData = canvas.toDataURL();
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = imageData;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive]);

  // Update context when color or line width changes
  useEffect(() => {
    if (!isActive || !isReady) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    if (tool === TOOLS.ERASER) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = lineWidth * 3;
      ctx.globalAlpha = 1;
      ctx.lineCap = 'round';
    } else if (tool === TOOLS.HIGHLIGHTER) {
      // Multiply blend keeps the underlying slide visible behind the colored stroke
      ctx.globalCompositeOperation = 'multiply';
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(14, lineWidth * 4);
      ctx.globalAlpha = 0.35;
      ctx.lineCap = 'square';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.globalAlpha = 1;
      ctx.lineCap = 'round';
    }
  }, [color, lineWidth, tool, isActive, isReady]);

  // Save current state to history
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const imageData = canvas.toDataURL();
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(imageData);
      if (newHistory.length > 20) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 19));
  }, [historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const newIndex = historyIndex - 1;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(img, 0, 0);
    };
    img.src = history[newIndex];
    setHistoryIndex(newIndex);
  }, [historyIndex, history]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const newIndex = historyIndex + 1;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(img, 0, 0);
    };
    img.src = history[newIndex];
    setHistoryIndex(newIndex);
  }, [historyIndex, history]);

  // Clear canvas
  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';
    saveToHistory();
  }, [saveToHistory]);

  // Get coordinates
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Mouse/Touch handlers
  const handleStart = (e) => {
    if (tool === TOOLS.POINTER) return;
    if (!isReady) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const coords = getCoords(e);
    setIsDrawing(true);
    setStartPoint(coords);
    
    if (tool === TOOLS.PENCIL || tool === TOOLS.ERASER || tool === TOOLS.HIGHLIGHTER) {
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      // Draw a dot for single clicks
      ctx.lineTo(coords.x + 0.1, coords.y + 0.1);
      ctx.stroke();
    }
  };

  const handleMove = (e) => {
    if (!isDrawing || tool === TOOLS.POINTER) return;
    if (!isReady) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const coords = getCoords(e);
    
    if (tool === TOOLS.PENCIL || tool === TOOLS.ERASER || tool === TOOLS.HIGHLIGHTER) {
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    }
  };

  const handleEnd = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    
    if (ctx && startPoint) {
      const coords = e ? getCoords(e) : startPoint;
      
      if (tool === TOOLS.LINE) {
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
      } else if (tool === TOOLS.CIRCLE) {
        const radius = Math.sqrt(
          Math.pow(coords.x - startPoint.x, 2) + Math.pow(coords.y - startPoint.y, 2)
        );
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (tool === TOOLS.RECTANGLE) {
        ctx.beginPath();
        ctx.strokeRect(startPoint.x, startPoint.y, coords.x - startPoint.x, coords.y - startPoint.y);
      }
    }
    
    setIsDrawing(false);
    setStartPoint(null);
    
    if (tool !== TOOLS.POINTER) {
      saveToHistory();
    }
  };

  // Handle close
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isActive) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'p' || e.key === 'P') {
        setTool(TOOLS.PENCIL);
      } else if (e.key === 'h' || e.key === 'H') {
        setTool(TOOLS.HIGHLIGHTER);
        // Default to yellow when activating highlighter
        setColor((c) => HIGHLIGHTER_COLORS.includes(c) ? c : HIGHLIGHTER_COLORS[0]);
      } else if (e.key === 'e' || e.key === 'E') {
        setTool(TOOLS.ERASER);
      } else if (e.key === 'v' || e.key === 'V') {
        setTool(TOOLS.POINTER);
      } else if ((e.key === 'c' || e.key === 'C') && !e.ctrlKey && !e.metaKey) {
        handleClear();
      } else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleClear, handleUndo, handleRedo, onClose]);

  if (!isActive) return null;

  return (
    <>
      {/* Full screen canvas layer */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0"
        style={{ 
          zIndex: 9998,
          cursor: tool === TOOLS.PENCIL ? 'crosshair' :
                  tool === TOOLS.HIGHLIGHTER ? 'crosshair' :
                  tool === TOOLS.ERASER ? 'cell' : 
                  tool === TOOLS.POINTER ? 'default' : 'crosshair',
          pointerEvents: tool === TOOLS.POINTER ? 'none' : 'auto',
          touchAction: 'none'
        }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        data-testid="drawing-canvas"
      />

      {/* Drawing Mode Indicator */}
      <div 
        className="fixed top-4 left-1/2 -translate-x-1/2"
        style={{ zIndex: 9999, pointerEvents: 'none' }}
      >
        <div className="bg-amber-500 text-black px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
          <Pencil className="w-4 h-4" />
          {t('drawing_mode_active')}
        </div>
      </div>

      {/* Floating Toolbar */}
      <AnimatePresence>
        {showToolbar && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2"
            style={{ zIndex: 9999 }}
            data-testid="drawing-toolbar"
          >
            <div className="bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-slate-700 p-3">
              <div className="flex items-center gap-2">
                {/* Tools */}
                <div className="flex items-center gap-1 pr-3 border-r border-slate-700">
                  <Button
                    variant={tool === TOOLS.POINTER ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setTool(TOOLS.POINTER)}
                    className="w-9 h-9"
                    title={t('pointer_tooltip')}
                    data-testid="tool-pointer"
                  >
                    <MousePointer className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={tool === TOOLS.PENCIL ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setTool(TOOLS.PENCIL)}
                    className="w-9 h-9"
                    title={t('pencil_tooltip')}
                    data-testid="tool-pencil"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={tool === TOOLS.HIGHLIGHTER ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => {
                      setTool(TOOLS.HIGHLIGHTER);
                      if (!HIGHLIGHTER_COLORS.includes(color)) setColor(HIGHLIGHTER_COLORS[0]);
                    }}
                    className="w-9 h-9"
                    title={t('highlighter_tooltip') || 'Highlighter (H)'}
                    data-testid="tool-highlighter"
                  >
                    <Highlighter className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={tool === TOOLS.LINE ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setTool(TOOLS.LINE)}
                    className="w-9 h-9"
                    title={t('tool_line')}
                    data-testid="tool-line"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={tool === TOOLS.CIRCLE ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setTool(TOOLS.CIRCLE)}
                    className="w-9 h-9"
                    title={t('tool_circle')}
                    data-testid="tool-circle"
                  >
                    <Circle className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={tool === TOOLS.RECTANGLE ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setTool(TOOLS.RECTANGLE)}
                    className="w-9 h-9"
                    title={t('tool_rectangle')}
                    data-testid="tool-rectangle"
                  >
                    <Square className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={tool === TOOLS.ERASER ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setTool(TOOLS.ERASER)}
                    className="w-9 h-9"
                    title={t('eraser_tooltip')}
                    data-testid="tool-eraser"
                  >
                    <Eraser className="w-4 h-4" />
                  </Button>
                </div>

                {/* Colors */}
                <div className="flex items-center gap-1 px-3 border-r border-slate-700">
                  {COLORS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setColor(c.value)}
                      className={`w-6 h-6 rounded-full transition-transform ${
                        color === c.value ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110' : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: c.value, border: c.value === '#ffffff' ? '1px solid #666' : 'none' }}
                      title={c.name}
                      data-testid={`color-${c.name.toLowerCase()}`}
                    />
                  ))}
                </div>

                {/* Line Width */}
                <div className="flex items-center gap-2 px-3 border-r border-slate-700">
                  <div 
                    className="rounded-full"
                    style={{ 
                      backgroundColor: color,
                      width: `${Math.max(8, lineWidth * 2)}px`,
                      height: `${Math.max(8, lineWidth * 2)}px`
                    }}
                  />
                  <Slider
                    value={[lineWidth]}
                    min={1}
                    max={20}
                    step={1}
                    onValueChange={([value]) => setLineWidth(value)}
                    className="w-20"
                    data-testid="line-width-slider"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 px-3 border-r border-slate-700">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleUndo}
                    disabled={historyIndex <= 0}
                    className="w-9 h-9"
                    title={t('undo_tooltip')}
                    data-testid="undo-btn"
                  >
                    <Undo className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRedo}
                    disabled={historyIndex >= history.length - 1}
                    className="w-9 h-9"
                    title={t('redo_tooltip')}
                    data-testid="redo-btn"
                  >
                    <Redo className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClear}
                    className="w-9 h-9 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    title={t('clear_all_tooltip')}
                    data-testid="clear-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Close */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="w-9 h-9 hover:bg-red-500/20 hover:text-red-400"
                  title={t('close_drawing_tooltip')}
                  data-testid="close-drawing-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Keyboard shortcuts hint */}
              <div className="mt-2 pt-2 border-t border-slate-700 flex items-center justify-center gap-4 text-xs text-slate-500">
                <span><kbd className="kbd-sm">P</kbd> {t('tool_pencil')}</span>
                <span><kbd className="kbd-sm">E</kbd> {t('tool_eraser')}</span>
                <span><kbd className="kbd-sm">V</kbd> {t('tool_pointer')}</span>
                <span><kbd className="kbd-sm">C</kbd> {t('clear_all')}</span>
                <span><kbd className="kbd-sm">Esc</kbd> {t('close')}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Toolbar Button (when hidden) */}
      {!showToolbar && (
        <button
          className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/90 p-3 rounded-full shadow-lg hover:bg-slate-800"
          style={{ zIndex: 9999 }}
          onClick={() => setShowToolbar(true)}
          data-testid="show-toolbar-btn"
        >
          <Pencil className="w-5 h-5 text-white" />
        </button>
      )}
    </>
  );
};

export default DrawingCanvas;
